import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShoppingBag, Truck, CalendarDays, Plus, Trash2, Loader2, Check, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function ShipForm({ order, onDone }) {
  const [f, setF] = useState({ carrier: "", tracking_number: "", tracking_url: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/shop/orders/${order.id}/ship`, f);
      toast.success(data.emailed ? "Marked shipped and emailed the buyer" : "Marked shipped (email not sent)");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 grid sm:grid-cols-4 gap-2" data-testid={`admin-order-ship-form-${order.id}`}>
      <Input placeholder="Carrier (USPS, UPS…)" value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })} data-testid="admin-order-carrier" />
      <Input placeholder="Tracking number" value={f.tracking_number} onChange={(e) => setF({ ...f, tracking_number: e.target.value })} data-testid="admin-order-tracking" />
      <Input placeholder="Tracking URL (optional)" value={f.tracking_url} onChange={(e) => setF({ ...f, tracking_url: e.target.value })} data-testid="admin-order-tracking-url" />
      <Button disabled={busy || !f.carrier || !f.tracking_number} onClick={submit} className="bg-blue-900 hover:bg-blue-800" data-testid="admin-order-ship-submit">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Truck size={14} className="mr-1.5" />} Mark shipped
      </Button>
    </div>
  );
}

function Orders() {
  const [rows, setRows] = useState([]);
  const [shipping, setShipping] = useState(null);
  const load = useCallback(() => api.get("/admin/shop/orders").then((r) => setRows(r.data)).catch(() => toast.error("Couldn't load orders")), []);
  useEffect(() => { load(); }, [load]);
  if (!rows.length) return <p className="text-sm text-slate-500" data-testid="admin-orders-empty">No paid orders yet.</p>;
  return (
    <div className="space-y-3">
      {rows.map((o) => {
        const a = o.shipping;
        return (
          <div key={o.id} data-testid={`admin-order-${o.id}`} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-slate-900">{o.item_name}</span>
              <span className="text-sm text-slate-500">${Number(o.price).toFixed(2)} · {fmt(o.paid_at)} · {o.buyer_name || o.buyer_email} ({o.buyer_email})</span>
              {o.kind === "physical" && (
                <span data-testid={`admin-order-status-${o.id}`} className={`ml-auto text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${o.fulfillment_status === "shipped" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                  {o.fulfillment_status === "shipped" ? "shipped" : "to ship"}
                </span>
              )}
            </div>
            {a && (
              <div className="mt-2 text-sm text-slate-700" data-testid={`admin-order-address-${o.id}`}>
                {a.name}{a.phone ? ` · ${a.phone}` : ""}<br />{[a.line1, a.line2].filter(Boolean).join(", ")}, {a.city}{a.state ? `, ${a.state}` : ""} {a.postal_code}, {a.country}
              </div>
            )}
            {o.fulfillment_status === "shipped" && <div className="mt-1 text-xs text-emerald-700">{o.carrier} · {o.tracking_number} · {fmt(o.shipped_at)}</div>}
            {o.kind === "physical" && o.fulfillment_status !== "shipped" && (shipping === o.id
              ? <ShipForm order={o} onDone={() => { setShipping(null); load(); }} />
              : <Button size="sm" variant="outline" className="mt-3" onClick={() => setShipping(o.id)} data-testid={`admin-order-ship-${o.id}`}><Truck size={13} className="mr-1.5" /> Mark shipped</Button>)}
          </div>
        );
      })}
    </div>
  );
}

function Availability() {
  const [cfg, setCfg] = useState(null);
  const [blockDate, setBlockDate] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/mentor-booking/settings").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return <Loader2 className="animate-spin text-slate-400" />;
  const setDay = (d, wins) => setCfg({ ...cfg, weekly: { ...cfg.weekly, [d]: wins } });
  const save = async () => {
    setSaving(true);
    try { setCfg((await api.put("/admin/mentor-booking/settings", { ...cfg, min_notice_hours: Math.max(24, Number(cfg.min_notice_hours) || 24) })).data); toast.success("Availability saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="admin-availability">
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="admin-availability-enabled" /> Accepting bookings</label>
        <label className="text-sm">Time zone <Input className="inline-block w-52 ml-1 h-9" value={cfg.timezone} onChange={(e) => setCfg({ ...cfg, timezone: e.target.value })} data-testid="admin-availability-tz" /></label>
        <label className="text-sm">Min notice (h) <Input type="number" min={24} className="inline-block w-20 ml-1 h-9" value={cfg.min_notice_hours} onChange={(e) => setCfg({ ...cfg, min_notice_hours: e.target.value })} onBlur={() => setCfg({ ...cfg, min_notice_hours: Math.max(24, Number(cfg.min_notice_hours) || 24) })} data-testid="admin-availability-min-notice" /></label>
        <label className="text-sm">Days ahead <Input type="number" className="inline-block w-20 ml-1 h-9" value={cfg.max_days_ahead} onChange={(e) => setCfg({ ...cfg, max_days_ahead: Number(e.target.value) })} /></label>
      </div>
      <p className="text-xs text-amber-700 font-semibold" data-testid="admin-availability-notice-rule">Rule: mentor calls and Residency intake calls can never be booked less than 24 hours in advance (enforced by the server).</p>
      <label className="block text-sm">Zoom / Meet link (sent in the confirmation)
        <Input value={cfg.meeting_link} onChange={(e) => setCfg({ ...cfg, meeting_link: e.target.value })} placeholder="https://zoom.us/j/…" data-testid="admin-availability-link" />
      </label>
      <div className="space-y-2">
        {DAYS.map((label, i) => {
          const wins = cfg.weekly[String(i)] || [];
          return (
            <div key={label} className="flex flex-wrap items-center gap-2" data-testid={`admin-availability-day-${i}`}>
              <span className="w-10 text-sm font-semibold text-slate-700">{label}</span>
              {wins.map((w, j) => (
                <span key={j} className="inline-flex items-center gap-1">
                  <Input type="time" className="h-9 w-28" value={w.start} onChange={(e) => setDay(i, wins.map((x, k) => (k === j ? { ...x, start: e.target.value } : x)))} />
                  –<Input type="time" className="h-9 w-28" value={w.end} onChange={(e) => setDay(i, wins.map((x, k) => (k === j ? { ...x, end: e.target.value } : x)))} />
                  <button type="button" onClick={() => setDay(i, wins.filter((_, k) => k !== j))} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                </span>
              ))}
              <Button size="sm" variant="ghost" onClick={() => setDay(i, [...wins, { start: "14:00", end: "17:00" }])} data-testid={`admin-availability-add-${i}`}><Plus size={13} /> Add hours</Button>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-slate-700">Blocked dates</span>
        {cfg.blocked_dates.map((d) => (
          <span key={d} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold">{d}
            <button type="button" onClick={() => setCfg({ ...cfg, blocked_dates: cfg.blocked_dates.filter((x) => x !== d) })}><X size={11} /></button></span>
        ))}
        <Input type="date" className="h-9 w-40" value={blockDate} onChange={(e) => setBlockDate(e.target.value)} data-testid="admin-availability-block-date" />
        <Button size="sm" variant="outline" disabled={!blockDate} onClick={() => { setCfg({ ...cfg, blocked_dates: [...new Set([...cfg.blocked_dates, blockDate])].sort() }); setBlockDate(""); }} data-testid="admin-availability-block-add">Block</Button>
      </div>
      <Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="admin-availability-save">{saving && <Loader2 size={14} className="mr-2 animate-spin" />} Save availability</Button>
    </div>
  );
}

function Bookings() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/admin/mentor-booking/bookings").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const act = async (b, action) => {
    const msg = { refund: "Cancel this call and refund the full amount in Stripe?", cancel: "Cancel this call WITHOUT a refund?" }[action];
    if (msg && !window.confirm(msg)) return;
    try {
      await api.post(`/admin/mentor-booking/bookings/${b.id}/${action}`);
      if (action === "refund") toast.success("Refunded and cancelled. The member was emailed.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  if (!rows.length) return <p className="text-sm text-slate-500" data-testid="admin-bookings-empty">No booked calls yet.</p>;
  return (
    <div className="space-y-2">
      {rows.map((b) => (
        <div key={b.id} data-testid={`admin-booking-${b.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
          <CalendarDays size={15} className="text-blue-800" />
          <span className="font-semibold text-slate-900">{fmt(b.start)}</span>
          <span className="text-sm text-slate-600">{b.user_name || b.user_email} · {b.user_email}</span>
          {b.note && <span className="text-xs text-slate-500 italic truncate max-w-xs">“{b.note}”</span>}
          {b.type === "intake" && <span className="text-[10px] uppercase font-bold text-blue-800 bg-blue-50 border border-blue-200 rounded px-1.5">intake</span>}
          <span className="ml-auto text-[10px] uppercase font-bold text-slate-500">{b.status}{b.refunded ? " · refunded" : ""}</span>
          {b.status === "confirmed" && (
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" onClick={() => act(b, "complete")} data-testid={`admin-booking-complete-${b.id}`}><Check size={13} /> Done</Button>
              {b.paid && <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={() => act(b, "refund")} data-testid={`admin-booking-refund-${b.id}`}><X size={13} /> Cancel & refund</Button>}
              <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => act(b, "cancel")} data-testid={`admin-booking-cancel-${b.id}`}>{b.paid ? "Cancel, no refund" : "Cancel"}</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminShop() {
  const [tab, setTab] = useState("orders");
  return (
    <div data-testid="admin-shop-root" className="space-y-6">
      <header>
        <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><ShoppingBag size={26} className="text-amber-600" /> Shop</h1>
        <p className="text-sm text-slate-500 mt-1">Paid orders with shipping details, plus the mentor-call booking calendar.</p>
      </header>
      <div className="flex gap-2">
        {[["orders", "Orders"], ["calls", "Mentor calls"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setTab(v)} data-testid={`admin-shop-tab-${v}`}
            className={`h-9 px-4 rounded-full text-sm font-semibold border ${tab === v ? "bg-blue-900 border-blue-900 text-white" : "bg-white border-slate-300 text-slate-700"}`}>{l}</button>
        ))}
      </div>
      {tab === "orders" ? <Orders /> : <div className="space-y-6"><Availability /><Bookings /></div>}
    </div>
  );
}
